import type { GameEmitter } from '../core/events';
import type { GameStatus } from '../core/types';
import type { GameMode } from '../core/config/modes';

export interface UIActions {
  start(): void;
  restart(): void;
  quit(): void;
  resume(): void;
  togglePause(): void;
  changeWeapon(delta: number): void;
  toggleReflections(enabled: boolean): void;
  toggleQuality(key: 'bloom' | 'debrisPhysics' | 'reduceMotion', enabled: boolean): void;
  selectMode(id: string): void;
  setPace(id: string): void;
}

export interface UIOptions {
  title: string;
  modes: GameMode[];
  selectedModeId: string;
  paces: Array<{ id: string; label: string }>;
  selectedPaceId: string;
  actions: UIActions;
}

const PAUSE = '⏸';
const PLAY = '▶';
const LESSON_COOLDOWN_MS = 2000;

// All DOM overlay (HUD + menus). Reacts to game events; reads nothing from the
// scene. Buttons are first-class controls, so the game is fully playable by
// touch — no keyboard required.
export class GameUI {
  private readonly el: HTMLDivElement;
  private scoreValue!: HTMLSpanElement;
  private scorePill!: HTMLDivElement;
  private bestValue!: HTMLSpanElement;
  private levelBadge!: HTMLDivElement;
  private weaponReadout!: HTMLDivElement;
  private banner!: HTMLDivElement;
  private lesson!: HTMLDivElement;
  private homeScreen!: HTMLDivElement;
  private homeSub!: HTMLParagraphElement;
  private homeInstructions!: HTMLParagraphElement;
  private modesEl!: HTMLDivElement;
  private paceEl!: HTMLDivElement;
  private pauseScreen!: HTMLDivElement;
  private overScreen!: HTMLDivElement;
  private overScore!: HTMLDivElement;
  private overBest!: HTMLDivElement;
  private pauseBtn!: HTMLButtonElement;
  private comboPill!: HTMLDivElement;
  private offs: Array<() => void> = [];
  private lastLessonAt = 0;
  private bannerTimer = 0;
  private lessonTimer = 0;
  private lastMultiplier = 1;

  constructor(
    private readonly root: HTMLElement,
    private readonly opts: UIOptions,
  ) {
    this.el = document.createElement('div');
    this.el.innerHTML = this.template();
    this.root.appendChild(this.el);
    this.cacheNodes();
    this.wire();
    this.renderModeCards(opts.selectedModeId);
    this.renderPace(opts.selectedPaceId);
  }

  bind(events: GameEmitter): void {
    this.offs.push(
      events.on('scoreChanged', ({ score, delta }) => {
        this.setScore(score);
        // Positive pops are placed at the kill location by the app layer
        // (it can project world→screen); penalties pop from the score pill.
        if (delta <= -10) this.popScore(`${delta}`, true);
      }),
      events.on('comboChanged', ({ combo, multiplier }) => this.setCombo(combo, multiplier)),
      events.on('statusChanged', ({ status }) => this.onStatus(status)),
      events.on('levelChanged', ({ level }) => this.setLevel(level)),
      events.on('weaponChanged', ({ weapon }) => this.setWeapon(weapon)),
      events.on('stageChanged', ({ banner, label }) => this.showBanner(banner, label)),
      events.on('outOfPoints', () => this.flashScore()),
      events.on('started', () => {
        this.setLevel(1);
        this.hideBanner();
      }),
      // Quit-to-home resets the run; don't leave a stale "LEVEL 7" badge.
      events.on('reset', () => {
        this.setLevel(1);
        this.hideBanner();
      }),
    );
  }

  /** Pulse the level badge — levels are the pacing beat, make them felt. */
  setLevel(level: number): void {
    this.levelBadge.textContent = `LEVEL ${level}`;
    this.levelBadge.classList.remove('cs-pulse');
    // Force a reflow so re-adding the class restarts the animation.
    void this.levelBadge.offsetWidth;
    this.levelBadge.classList.add('cs-pulse');
  }

  setWeapon(weapon: number): void {
    this.weaponReadout.textContent = String(weapon);
  }

  /** Streak meter: hidden until a real streak forms, pulses on tier-ups. */
  setCombo(combo: number, multiplier: number): void {
    if (combo < 2) {
      this.comboPill.hidden = true;
      this.lastMultiplier = 1;
      return;
    }
    this.comboPill.hidden = false;
    this.comboPill.textContent = multiplier > 1 ? `COMBO ${combo} ×${multiplier}` : `COMBO ${combo}`;
    if (multiplier > this.lastMultiplier) {
      this.comboPill.classList.remove('cs-pulse');
      void this.comboPill.offsetWidth;
      this.comboPill.classList.add('cs-pulse');
    }
    this.lastMultiplier = multiplier;
  }

  /** Float a score pop at an arbitrary screen point (kill location). */
  popScoreAt(text: string, x: number, y: number): void {
    const pop = document.createElement('div');
    pop.className = 'cs-score-pop cs-score-pop-at';
    pop.style.left = `${x}px`;
    pop.style.top = `${y}px`;
    pop.textContent = text;
    this.el.appendChild(pop);
    pop.addEventListener('animationend', () => pop.remove());
  }

  /** Positive toast (power-ups): same slot as the lesson, golden. */
  showToast(text: string): void {
    this.lesson.textContent = text;
    this.lesson.hidden = false;
    this.lesson.classList.add('cs-lesson-good');
    this.lesson.classList.remove('cs-lesson-in');
    void this.lesson.offsetWidth;
    this.lesson.classList.add('cs-lesson-in');
    window.clearTimeout(this.lessonTimer);
    this.lessonTimer = window.setTimeout(() => {
      this.lesson.hidden = true;
      this.lesson.classList.remove('cs-lesson-good');
    }, 1500);
  }

  /** Big friendly stage announcement ("Make ten! 3 needs 7"). */
  showBanner(text: string, sub: string): void {
    this.banner.innerHTML = '';
    const main = document.createElement('div');
    main.className = 'cs-banner-main';
    main.textContent = text;
    const small = document.createElement('div');
    small.className = 'cs-banner-sub';
    small.textContent = sub;
    this.banner.append(main, small);
    this.banner.hidden = false;
    this.banner.classList.remove('cs-banner-in');
    void this.banner.offsetWidth;
    this.banner.classList.add('cs-banner-in');
    window.clearTimeout(this.bannerTimer);
    this.bannerTimer = window.setTimeout(() => this.hideBanner(), 3000);
  }

  private hideBanner(): void {
    this.banner.hidden = true;
  }

  /** Teach-on-miss toast, e.g. "3 needs 7 — you fired 5". Rate-limited so
   *  fast play doesn't turn it into a strobe. */
  showLesson(text: string): void {
    const now = performance.now();
    if (now - this.lastLessonAt < LESSON_COOLDOWN_MS) return;
    this.lastLessonAt = now;
    this.lesson.textContent = text;
    this.lesson.hidden = false;
    this.lesson.classList.remove('cs-lesson-in');
    void this.lesson.offsetWidth;
    this.lesson.classList.add('cs-lesson-in');
    window.clearTimeout(this.lessonTimer);
    this.lessonTimer = window.setTimeout(() => {
      this.lesson.hidden = true;
    }, 1800);
  }

  /** Out-of-points feedback: shake the score pill red. */
  private flashScore(): void {
    this.scorePill.classList.remove('cs-score-flash');
    void this.scorePill.offsetWidth;
    this.scorePill.classList.add('cs-score-flash');
  }

  /** Float a "+100" / "−10" from the score chip. */
  private popScore(text: string, penalty: boolean): void {
    const pop = document.createElement('div');
    pop.className = penalty ? 'cs-score-pop cs-score-pop-penalty' : 'cs-score-pop';
    pop.textContent = text;
    this.el.appendChild(pop);
    pop.addEventListener('animationend', () => pop.remove());
  }

  setScore(score: number): void {
    this.scoreValue.textContent = String(score).padStart(10, '0');
  }

  setBest(best: number): void {
    this.bestValue.textContent = best > 0 ? `BEST ${best}` : '';
  }

  /** Update home-card copy when the selected mode changes. */
  setModeInfo(subtitle: string, instructions: string): void {
    this.homeSub.textContent = subtitle;
    this.homeInstructions.textContent = instructions;
  }

  setModeBest(modeId: string, best: number): void {
    const el = this.modesEl.querySelector<HTMLElement>(`[data-best="${modeId}"]`);
    if (el) el.textContent = best > 0 ? `Best ${best}` : '';
  }

  setReflections(enabled: boolean): void {
    const box = this.el.querySelector<HTMLInputElement>('#cs-reflections');
    if (box) box.checked = enabled;
  }

  setQualityChecks(opts: { bloom: boolean; debrisPhysics: boolean; reduceMotion: boolean }): void {
    const set = (id: string, v: boolean): void => {
      const box = this.el.querySelector<HTMLInputElement>(id);
      if (box) box.checked = v;
    };
    set('#cs-bloom', opts.bloom);
    set('#cs-debris', opts.debrisPhysics);
    set('#cs-motion', opts.reduceMotion);
  }

  showGameOver(score: number, best: number): void {
    this.overScore.textContent = String(score);
    this.overBest.textContent = best > 0 ? `Best ${best}` : '';
  }

  dispose(): void {
    for (const off of this.offs) off();
    this.offs = [];
    window.clearTimeout(this.bannerTimer);
    window.clearTimeout(this.lessonTimer);
    this.el.remove();
  }

  private onStatus(status: GameStatus): void {
    this.homeScreen.hidden = status !== 'idle';
    this.pauseScreen.hidden = status !== 'paused';
    this.overScreen.hidden = status !== 'gameover';
    this.pauseBtn.textContent = status === 'paused' ? PLAY : PAUSE;
  }

  private renderModeCards(selectedId: string): void {
    this.modesEl.innerHTML = '';
    for (const mode of this.opts.modes) {
      const card = document.createElement('button');
      card.className = 'cs-mode-card' + (mode.id === selectedId ? ' active' : '');
      card.dataset.mode = mode.id;
      const name = document.createElement('div');
      name.className = 'cs-mode-name';
      name.textContent = mode.label;
      const desc = document.createElement('div');
      desc.className = 'cs-mode-desc';
      desc.textContent = mode.description;
      const best = document.createElement('div');
      best.className = 'cs-mode-best';
      best.dataset.best = mode.id;
      card.append(name, desc, best);
      this.modesEl.appendChild(card);
    }
  }

  private renderPace(selectedId: string): void {
    this.paceEl.innerHTML = '';
    for (const pace of this.opts.paces) {
      const btn = document.createElement('button');
      btn.className = 'cs-pace-btn' + (pace.id === selectedId ? ' active' : '');
      btn.dataset.pace = pace.id;
      btn.textContent = pace.label;
      this.paceEl.appendChild(btn);
    }
  }

  private cacheNodes(): void {
    const q = <T extends HTMLElement>(sel: string): T => this.el.querySelector(sel) as T;
    this.scoreValue = q('#cs-score-value');
    this.scorePill = q('#cs-score-pill');
    this.comboPill = q('#cs-combo');
    this.bestValue = q('#cs-best-value');
    this.levelBadge = q('#cs-level');
    this.weaponReadout = q('#cs-weapon');
    this.banner = q('#cs-banner');
    this.lesson = q('#cs-lesson');
    this.homeScreen = q('#cs-home');
    this.homeSub = q('#cs-home-sub');
    this.homeInstructions = q('#cs-home-instructions');
    this.modesEl = q('#cs-modes');
    this.paceEl = q('#cs-pace');
    this.pauseScreen = q('#cs-pause');
    this.overScreen = q('#cs-over');
    this.overScore = q('#cs-over-score');
    this.overBest = q('#cs-over-best');
    this.pauseBtn = q('#cs-pause-btn');
  }

  private wire(): void {
    const { actions } = this.opts;
    const on = (sel: string, fn: () => void): void => {
      const node = this.el.querySelector(sel);
      node?.addEventListener('click', (e) => {
        e.stopPropagation();
        fn();
      });
    };
    on('#cs-start', actions.start);
    on('#cs-plus', () => actions.changeWeapon(1));
    on('#cs-minus', () => actions.changeWeapon(-1));
    on('#cs-pause-btn', actions.togglePause);
    on('#cs-new', actions.restart);
    on('#cs-resume', actions.resume);
    on('#cs-restart', actions.restart);
    on('#cs-quit', actions.quit);
    on('#cs-replay', actions.restart);
    on('#cs-home-btn', actions.quit);

    // Mode cards and pace buttons are re-renderable — delegate on containers.
    this.el.querySelector('#cs-modes')?.addEventListener('click', (e) => {
      const card = (e.target as HTMLElement).closest<HTMLElement>('.cs-mode-card');
      if (!card?.dataset.mode) return;
      e.stopPropagation();
      this.modesEl.querySelectorAll('.cs-mode-card').forEach((c) => c.classList.remove('active'));
      card.classList.add('active');
      actions.selectMode(card.dataset.mode);
    });
    this.el.querySelector('#cs-pace')?.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>('.cs-pace-btn');
      if (!btn?.dataset.pace) return;
      e.stopPropagation();
      this.paceEl.querySelectorAll('.cs-pace-btn').forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
      actions.setPace(btn.dataset.pace);
    });

    const reflections = this.el.querySelector('#cs-reflections') as HTMLInputElement;
    reflections?.addEventListener('change', () => actions.toggleReflections(reflections.checked));
    const bloom = this.el.querySelector('#cs-bloom') as HTMLInputElement;
    bloom?.addEventListener('change', () => actions.toggleQuality('bloom', bloom.checked));
    const debris = this.el.querySelector('#cs-debris') as HTMLInputElement;
    debris?.addEventListener('change', () => actions.toggleQuality('debrisPhysics', debris.checked));
    const motion = this.el.querySelector('#cs-motion') as HTMLInputElement;
    motion?.addEventListener('change', () => actions.toggleQuality('reduceMotion', motion.checked));
  }

  private template(): string {
    const { title } = this.opts;
    return `
    <div class="cs-hud">
      <div class="cs-score" id="cs-score-pill">SCORE <span id="cs-score-value">0000000100</span>
        <span class="cs-best" id="cs-best-value"></span>
      </div>
      <div class="cs-level" id="cs-level">LEVEL 1</div>
      <div class="cs-combo" id="cs-combo" hidden></div>
      <div class="cs-banner" id="cs-banner" hidden></div>
      <div class="cs-lesson" id="cs-lesson" hidden></div>
      <div class="cs-top-left"><button id="cs-pause-btn" class="cs-circle">${PAUSE}</button></div>
      <div class="cs-top-right"><button id="cs-new" class="cs-circle">↻</button></div>
      <div class="cs-weapons">
        <button id="cs-minus" class="cs-circle">−</button>
        <div class="cs-weapon-readout" id="cs-weapon">0</div>
        <button id="cs-plus" class="cs-circle">+</button>
      </div>
      <div class="cs-settings">
        <label><input type="checkbox" id="cs-reflections" checked /> Reflections</label>
        <label><input type="checkbox" id="cs-bloom" /> Glow</label>
        <label><input type="checkbox" id="cs-debris" /> Physics debris</label>
        <label><input type="checkbox" id="cs-motion" /> Reduce motion</label>
      </div>
    </div>

    <div class="cs-screen" id="cs-home">
      <div class="cs-card cs-card-home">
        <h1>${title}</h1>
        <p class="cs-sub" id="cs-home-sub"></p>
        <div class="cs-modes" id="cs-modes"></div>
        <div class="cs-pace-row">
          <span class="cs-pace-label">Pace</span>
          <div class="cs-pace" id="cs-pace"></div>
        </div>
        <p id="cs-home-instructions"></p>
        <button id="cs-start" class="cs-btn">Start</button>
      </div>
    </div>

    <div class="cs-screen" id="cs-pause" hidden>
      <div class="cs-card">
        <h1>Paused</h1>
        <div class="cs-row">
          <button id="cs-resume" class="cs-btn">${PLAY} Resume</button>
          <button id="cs-restart" class="cs-btn secondary">↻ Restart</button>
        </div>
        <button id="cs-quit" class="cs-btn secondary">Quit</button>
      </div>
    </div>

    <div class="cs-screen" id="cs-over" hidden>
      <div class="cs-card">
        <h1>Game Over</h1>
        <div class="cs-gameover-score" id="cs-over-score">0</div>
        <p id="cs-over-best"></p>
        <div class="cs-row">
          <button id="cs-replay" class="cs-btn">Play Again</button>
          <button id="cs-home-btn" class="cs-btn secondary">Home</button>
        </div>
      </div>
    </div>`;
  }
}
